/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { changeProblem, effectiveChanges } from "~/features/feature-flags/shared/changeRules"
import type { FlagChangeRequest } from "~/features/feature-flags/shared/contracts"
import {
  countTargets,
  onOffLabel,
  type TargetNoun,
} from "~/features/feature-flags/client/model/labels"
import { flagName, type FeatureFlagKey } from "~/features/feature-flags/shared/registry"
import type {
  FlagNames,
  FlagPanel,
  FlagRules,
} from "~/features/feature-flags/client/model/flagPanel"

/** A flag change someone asked for: company or user overrides set to `value` (null resets them
 * to the default), or a new default. */
export type ChangeRequest =
  | {
      kind: "targets"
      scope: "org" | "user"
      flagKey: FeatureFlagKey
      targetIds: readonly string[]
      value: boolean | null
    }
  | { kind: "default"; flagKey: FeatureFlagKey; value: boolean }

/** Sets company overrides (scope "org") or user overrides ("user") to `value`; null resets. */
export const targetsRequest = (
  scope: "org" | "user",
  flagKey: FeatureFlagKey,
  targetId: string,
  value: boolean | null,
): ChangeRequest => ({ kind: "targets", scope, flagKey, targetIds: [targetId], value })

/** A change request described for the person applying it. */
export type ChangeDescription = {
  title: string
  /** What gets sent: only the changes that do something. */
  batch: { changes: FlagChangeRequest[] }
  /** The companies or users that change. */
  targets: { id: string; name: string }[]
  /** Shown above the note; the change can still be applied. */
  warning: string | null
  /** Stops the change from being applied, with the reason. */
  blocker: string | null
  applyLabel: string
}

const APPLY_NOW = "Apply now"

export const verb = (value: boolean | null) => {
  if (value === null) return "Reset"
  return value ? "Turn on" : "Turn off"
}

const noOpBlocker = (
  count: number,
  value: boolean | null,
  noun: TargetNoun,
  singleName: string,
): string => {
  if (value === null) {
    return count === 1
      ? `${singleName} already follows the default.`
      : `All ${countTargets(count, noun)} already follow the default.`
  }
  return count === 1
    ? `${singleName} is already set to ${onOffLabel(value)}.`
    : `All ${countTargets(count, noun)} are already set to ${onOffLabel(value)}.`
}

const userLabel = (user: FlagNames["user"]) => (userId: string) => {
  const found = user(userId)
  return found ? `${found.name} (${found.email})` : userId
}

function describeTargets(
  { snapshot, companyName, user, isStaffUser, userOrgIds, isOffList, lockReason }: FlagPanel,
  { flagKey, scope, targetIds, value }: Extract<ChangeRequest, { kind: "targets" }>,
): ChangeDescription {
  const noun: TargetNoun = scope === "org" ? "company" : "user"
  const name = scope === "org" ? companyName : userLabel(user)
  const changes = effectiveChanges(
    targetIds.map((targetId) => ({
      flagKey,
      scope,
      targetId,
      value,
      expected: snapshot.override({ flagKey, scope, targetId }),
    })),
  )
  const changing = changes.map(({ targetId }) => targetId)
  const shown = changing.length > 0 ? changing : targetIds
  const who = shown.length === 1 ? name(shown[0] ?? "") : countTargets(shown.length, noun)
  const isOff = (id: string) =>
    scope === "org"
      ? isOffList(flagKey, id)
      : !isStaffUser(id) &&
        userOrgIds(id).length > 0 &&
        userOrgIds(id).every((orgId) => isOffList(flagKey, orgId))
  const offList = value === true ? changing.filter(isOff) : []

  return {
    title: `${verb(value)} ${flagName(flagKey)} for ${who}`,
    batch: { changes },
    targets: changing.map((id) => ({ id, name: name(id) })),
    warning:
      offList.length > 0
        ? `${flagName(flagKey)} is a custom flag built for other companies. Turning it on for ${offList.map(name).join(", ")} gives them a feature that wasn't made for them.`
        : null,
    blocker:
      lockReason(flagKey, scope) ??
      (changing.length === 0
        ? noOpBlocker(targetIds.length, value, noun, name(targetIds[0] ?? ""))
        : null),
    applyLabel: offList.length > 0 ? "Turn on anyway" : APPLY_NOW,
  }
}

function describeDefault(
  { snapshot, lock }: FlagRules,
  { flagKey, value }: Extract<ChangeRequest, { kind: "default" }>,
): ChangeDescription {
  const name = flagName(flagKey)
  const state = onOffLabel(value).toLowerCase()
  const target = { flagKey, scope: "default", targetId: "" } as const
  const change: FlagChangeRequest = { ...target, value, expected: snapshot.override(target) }
  const alreadyDefault = snapshot.defaultValue(flagKey) === value

  return {
    title: `Turn ${name} ${state} by default`,
    batch: { changes: [change] },
    targets: [],
    warning: null,
    blocker:
      changeProblem(change, lock) ??
      (alreadyDefault ? `${name} is already ${state} by default.` : null),
    applyLabel: APPLY_NOW,
  }
}

/**
 * Everything the change dialog shows for a request, from the panel data as it is now: so a refetch
 * while the dialog is open updates what it says and what it sends.
 */
export function describeChange(inputs: FlagPanel, request: ChangeRequest): ChangeDescription {
  if (request.kind === "targets") return describeTargets(inputs, request)
  return describeDefault(inputs, request)
}
